import { Box } from '@osmosis/react-ui/components/Box';
import React from 'react';
import { useIntl } from 'react-intl';
import { QuizFilterState } from '../../types/quiz';

type QuizSummaryLegendProps = {
  correctCount: number;
  incorrectCount: number;
  remainingCount: number;
  filters: QuizFilterState;
};
/**
 * Renders a summary of quiz results.
 */
function QuizSummaryLegend({
  correctCount,
  incorrectCount,
  remainingCount,
  filters,
}: Readonly<QuizSummaryLegendProps>): React.ReactElement {
  const intl = useIntl();
  const showCorrectLegend =
    filters.correctHigh || filters.correctMedium || filters.correctLow;
  const showIncorrectLegend =
    filters.incorrectHigh || filters.incorrectMedium || filters.incorrectLow;
  const showRemainingLegend = filters.remaining;

  const noFiltersSelected = !(
    showCorrectLegend ||
    showIncorrectLegend ||
    showRemainingLegend
  );
  const legendItems = [
    {
      color: '#16A34A',
      border: undefined,
      count: correctCount,
      labelId: 'quiz.summary.correct',
      defaultLabel: 'correct',
      key: 'correct',
    },
    {
      color: '#B30000',
      border: undefined,
      count: incorrectCount,
      labelId: 'quiz.summary.incorrect',
      defaultLabel: 'incorrect',
      key: 'incorrect',
    },
    {
      color: '#F4F4F4',
      border: '1px solid #ccc',
      count: remainingCount,
      labelId: 'quiz.summary.remaining',
      defaultLabel: 'remaining',
      key: 'remaining',
    },
  ];

  // Filter legendItems based on selected filters
  const filteredLegendItems = noFiltersSelected
    ? legendItems
    : legendItems.filter((item) => {
        if (item.key === 'correct') return showCorrectLegend;
        if (item.key === 'incorrect') return showIncorrectLegend;
        if (item.key === 'remaining') return showRemainingLegend;
        return false;
      });

  return (
    <Box className="flex gap-10 mt-[32px] items-center">
      {filteredLegendItems.map((item, idx) => (
        <Box key={item.labelId} className="flex items-center gap-2">
          <Box
            className="inline-block w-4 h-4 rounded"
            sx={{
              background: item.color,
              border: item.border,
            }}
            data-testid={`quiz-summary-legend-color-${item.key}`}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <span
              className="text-gray-700 text-[16px]"
              data-testid={`quiz-summary-legend-count-${item.key}`}
            >
              {item.count}
            </span>
            <span
              className="text-gray-700 text-[16px]"
              data-testid={`quiz-summary-legend-label-${item.key}`}
            >
              {intl.formatMessage({
                id: item.labelId,
                defaultMessage: item.defaultLabel,
              })}
            </span>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default QuizSummaryLegend;
